import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';
import { followUser, unfollowUser } from '../../api/users';

/**
 * User card component for displaying user info in lists
 * @param {Object} props - Component props
 * @param {Object} props.user - User data
 * @param {boolean} props.showFollowButton - Whether to display follow button
 * @param {boolean} props.showMessageButton - Whether to display message button
 * @param {Function} props.onFollowChange - Callback for follow status change
 * @returns {JSX.Element} UserCard component
 */
const UserCard = ({ 
  user, 
  showFollowButton = false, 
  showMessageButton = false,
  onFollowChange 
}) => {
  const { user: currentUser } = useAuth();
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [followState, setFollowState] = useState({
    isFollowing: user.isFollowing || false,
    loading: false,
    error: null
  });

  // Check if this is the current user
  useEffect(() => {
    if (!currentUser) return;
    
    const currentUserId = currentUser._id || 
                        (currentUser.data && currentUser.data.user && currentUser.data.user._id);
    
    setIsCurrentUser(currentUserId === user._id);
  }, [currentUser, user._id]);

  // Handle follow/unfollow action
  const handleFollowToggle = async () => {
    if (isCurrentUser || followState.loading) return;
    
    try {
      setFollowState(prev => ({ ...prev, loading: true }));
      
      if (followState.isFollowing) {
        // Call unfollow API
        await unfollowUser(user._id);
      } else {
        // Call follow API
        await followUser(user._id);
      }
      
      // Update local state
      const newFollowState = !followState.isFollowing;
      setFollowState({
        isFollowing: newFollowState,
        loading: false,
        error: null
      });
      
      // Notify parent component if callback is provided
      if (onFollowChange) {
        onFollowChange(newFollowState, user._id);
      }
    } catch (err) {
      console.error('Failed to update follow status:', err);
      setFollowState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to update follow status'
      }));
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-4 hover:shadow-sm transition-shadow">
      <Link to={`/profile/${user.username}`}>
        <Avatar
          src={user.profilePicture}
          alt={user.name || user.username}
          size="md"
          isOnline={user.isOnline}
        />
      </Link>
      
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${user.username}`} className="hover:underline">
          <h3 className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</h3>
        </Link>
        <Link to={`/profile/${user.username}`} className="hover:underline">
          <p className="text-sm text-gray-500 truncate">@{user.username}</p>
        </Link>
        {user.bio && (
          <p className="mt-1 text-sm text-gray-700 line-clamp-2">{user.bio}</p>
        )}
      </div>
      
      <div className="flex flex-shrink-0 space-x-2">
        {showFollowButton && !isCurrentUser && (
          <Button 
            variant={followState.isFollowing ? "light" : "primary"}
            size="sm"
            onClick={handleFollowToggle}
            isLoading={followState.loading}
            className={followState.isFollowing ? "hover:bg-red-50 hover:text-red-600" : ""}
          >
            {followState.isFollowing ? 'Unfollow' : 'Follow'}
          </Button>
        )}
        
        {showMessageButton && !isCurrentUser && (
          <Link to={`/chat/${user._id}`}>
            <Button variant="light" size="sm">
              Message
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

UserCard.propTypes = {
  user: PropTypes.object.isRequired,
  showFollowButton: PropTypes.bool,
  showMessageButton: PropTypes.bool,
  onFollowChange: PropTypes.func
};

export default UserCard;